import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/lib/hooks/use-toast';
import { storeConnectionsApi } from '../services';
import type { StoreConnection } from '../types';
import { Loader2, CheckCircle2, XCircle, Wifi } from 'lucide-react';

interface ConnectStoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editConnection?: StoreConnection | null;
  onSaved: () => void;
}

export function ConnectStoreDialog({
  open,
  onOpenChange,
  editConnection,
  onSaved,
}: ConnectStoreDialogProps) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [storeSecret, setStoreSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      if (editConnection) {
        setName(editConnection.name);
        setStoreUrl(editConnection.storeUrl);
        setStoreSecret(editConnection.storeSecret);
      } else {
        setName('');
        setStoreUrl('');
        setStoreSecret('');
      }
      setTestResult(null);
      setErrors({});
    }
  }, [open, editConnection]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'El nombre es requerido';
    if (!storeUrl.trim()) {
      newErrors.storeUrl = 'La URL es requerida';
    } else {
      try {
        new URL(storeUrl);
      } catch {
        newErrors.storeUrl = 'La URL no tiene un formato válido';
      }
    }
    if (!storeSecret.trim()) newErrors.storeSecret = 'El secret es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTest = async () => {
    if (!editConnection) {
      toast({ title: 'Guarda la conexión primero para poder probarla', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const raw = await storeConnectionsApi.test(editConnection.id);
      const result = { ok: raw.status === 'ok', message: raw.status };
      setTestResult(result);
      toast({
        title: result.ok ? 'Conexión exitosa' : 'Error de conexión',
        description: result.message,
        variant: result.ok ? 'default' : 'destructive',
      });
    } catch (err: any) {
      setTestResult({ ok: false, message: err?.message || 'Error desconocido' });
      toast({
        title: 'Error al probar conexión',
        description: err?.message,
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editConnection) {
        await storeConnectionsApi.update(editConnection.id, { name, storeUrl, storeSecret });
        toast({ title: 'Conexión actualizada' });
      } else {
        await storeConnectionsApi.create({ name, storeUrl, storeSecret });
        toast({ title: 'Tienda conectada exitosamente' });
      }
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: 'Error al guardar', description: err?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editConnection ? 'Editar conexión' : 'Conectar tienda'}</DialogTitle>
          <DialogDescription>
            {editConnection
              ? 'Modifica los datos de la conexión a tu tienda.'
              : 'Conecta tu tienda para sincronizar precios automáticamente.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="conn-name">Nombre</Label>
            <Input
              id="conn-name"
              placeholder="Mi Tienda Shopify"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-url">URL de la tienda</Label>
            <Input
              id="conn-url"
              placeholder="https://mi-tienda.myshopify.com"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
            />
            {errors.storeUrl && <p className="text-xs text-destructive">{errors.storeUrl}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-secret">Store Secret / API Key</Label>
            <Input
              id="conn-secret"
              type="password"
              placeholder="shpat_xxxxxxxxxxxx"
              value={storeSecret}
              onChange={(e) => setStoreSecret(e.target.value)}
            />
            {errors.storeSecret && <p className="text-xs text-destructive">{errors.storeSecret}</p>}
          </div>

          {editConnection && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Wifi className="w-4 h-4 mr-1" />
                )}
                Probar conexión
              </Button>
              {testResult && (
                <span
                  className={`flex items-center gap-1 text-sm ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}
                >
                  {testResult.ok ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {testResult.message}
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {editConnection ? 'Guardar cambios' : 'Conectar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
